/** Campaign Account payout eligibility (GET .../campaign-account/payout-info) */

export type CampaignPayoutBlockReason = 'awaiting_scheduled_payout' | 'below_minimum' | 'pending_payout' | string;

export interface ICampaignPayoutSchedule {
    cadence: string;
    day: string;
    local_time: string;
    timezone: string;
    cron: string;
}

export interface ICampaignPendingPayout {
    id?: string;
    amount_cents?: number;
    status?: string;
    created_at?: string;
}

export interface ICampaignPayoutInfo {
    balance_cents: number;
    balance?: number;
    currency?: string;
    minimum_withdrawal_cents: number;
    meets_minimum: boolean;
    has_active_subscription: boolean;
    can_request_now: boolean;
    withdrawal_enabled: boolean;
    block_reason?: CampaignPayoutBlockReason | null;
    pending_payout?: ICampaignPendingPayout | null;
    next_scheduled_payout_at?: string | null;
    next_scheduled_payout_timezone?: string;
    schedule?: ICampaignPayoutSchedule;
    auto_transfer_enabled?: boolean;
    message?: string;
    total_earned_cents?: number;
    total_withdrawn_cents?: number;
}

export function formatCampaignScheduledPayout(iso?: string | null, timezone?: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    try {
        return d.toLocaleString('en-US', {
            timeZone: timezone || 'America/New_York',
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            timeZoneName: 'short',
        });
    } catch {
        return d.toLocaleString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    }
}

/** Merge payout nested object from balance endpoint when dedicated payout-info is unavailable. */
export function normalizeCampaignPayoutInfo(raw: any): ICampaignPayoutInfo {
    const payout = raw?.payout ?? {};
    const balanceCents = raw?.balance_cents ?? payout.balance_cents ?? 0;
    const minCents = raw?.minimum_withdrawal_cents ?? payout.minimum_withdrawal_cents ?? 4000;
    const canRequest =
        payout.can_request_now ?? raw?.can_request_now ?? (raw?.withdrawable && balanceCents >= minCents) ?? false;
    const withdrawalEnabled = payout.withdrawal_enabled ?? raw?.withdrawal_enabled ?? canRequest;

    return {
        balance_cents: balanceCents,
        balance: raw?.balance ?? balanceCents / 100,
        currency: raw?.currency ?? 'USD',
        minimum_withdrawal_cents: minCents,
        meets_minimum: payout.meets_minimum ?? raw?.meets_minimum ?? balanceCents >= minCents,
        has_active_subscription: payout.has_active_subscription ?? raw?.has_active_subscription ?? false,
        can_request_now: Boolean(canRequest),
        withdrawal_enabled: Boolean(withdrawalEnabled),
        block_reason: payout.block_reason ?? raw?.block_reason ?? null,
        pending_payout: payout.pending_payout ?? raw?.pending_payout ?? null,
        next_scheduled_payout_at: payout.next_scheduled_payout_at ?? raw?.next_scheduled_payout_at ?? null,
        next_scheduled_payout_timezone:
            payout.next_scheduled_payout_timezone ??
            raw?.next_scheduled_payout_timezone ??
            payout.schedule?.timezone ??
            'America/New_York',
        schedule: payout.schedule ?? raw?.schedule,
        auto_transfer_enabled: payout.auto_transfer_enabled ?? raw?.auto_transfer_enabled ?? false,
        message: payout.message ?? raw?.message,
        total_earned_cents: raw?.total_earned_cents,
        total_withdrawn_cents: raw?.total_withdrawn_cents,
    };
}
